import type React from 'react'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { createOrganization } from '@/lib/org-api-client'
import { Button } from '@knownow/ui'

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

export function OnboardingOrgPage(): React.ReactElement {
  const [name, setName] = useState('')
  const [slug, setSlug] = useState('')
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()

  const handleNameChange = (value: string): void => {
    setName(value)
    if (!slugManuallyEdited) {
      setSlug(slugify(value))
    }
  }

  const handleSlugChange = (value: string): void => {
    setSlugManuallyEdited(true)
    setSlug(slugify(value))
  }

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault()
    setError(null)
    setLoading(true)

    try {
      await createOrganization(name, slug)
      navigate(`/${slug}/plans`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create organization')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-neutral-50" data-testid="onboarding-org">
      <div className="w-full max-w-sm space-y-6 rounded-lg bg-white p-8 shadow-md">
        <h1 className="text-display-sm font-serif text-center">Create your organization</h1>
        <p className="text-sm text-neutral-500 text-center">This is where your team collaborates</p>

        {error && (
          <div className="rounded-md bg-error-50 p-3 text-sm text-error-700">{error}</div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="orgName" className="text-sm font-medium text-neutral-700">Organization name</label>
            <input
              id="orgName"
              type="text"
              value={name}
              onChange={(e) => handleNameChange(e.target.value)}
              required
              className="mt-1 block w-full rounded-md border border-neutral-300 px-3 py-2 text-sm"
              data-testid="org-name-input"
            />
          </div>
          <div>
            <label htmlFor="orgSlug" className="text-sm font-medium text-neutral-700">URL slug</label>
            <input
              id="orgSlug"
              type="text"
              value={slug}
              onChange={(e) => handleSlugChange(e.target.value)}
              required
              pattern="[a-z0-9][a-z0-9-]*[a-z0-9]"
              className="mt-1 block w-full rounded-md border border-neutral-300 px-3 py-2 text-sm font-mono"
              data-testid="org-slug-input"
            />
            <p className="mt-1 text-xs text-neutral-400">knownow.app/{slug || '...'}</p>
          </div>
          <Button type="submit" disabled={loading || !slug} data-testid="org-submit">
            {loading ? 'Creating...' : 'Create organization'}
          </Button>
        </form>
      </div>
    </div>
  )
}
